import React from "react";
import type { ApiUser } from "../types";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import type { RowSelectionState } from "@tanstack/react-table";
import MobileListWrapper from "@/components/mobile-list-wrapper";

interface ListContentProps {
  data: ApiUser[];
  isLoading: boolean;
  className?: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  emptyListComponent: React.ReactElement;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
}

const ListContent = ({
  data,
  isLoading,
  className,
  page,
  pageCount,
  onPageChange,
  emptyListComponent,
  rowSelection,
  onRowSelectionChange,
}: ListContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <MobileListWrapper
      data={data}
      isLoading={isLoading}
      className={className}
      page={page}
      pageCount={pageCount}
      onPageChange={onPageChange}
      emptyListComponent={emptyListComponent}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      onItemClick={(user) => navigate(ROUTES.USER_DETAILS(user.id))}
      renderItem={(user) => (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {user.first_name} {user.last_name}
            </h3>
            <Badge
              variant={user.status === "active" ? "success" : "destructive"}
            >
              {user.status === "active"
                ? t("keys.active")
                : t("keys.desactivated")}
            </Badge>
          </div>
          <p className="text-gray-400 text-sm">{user.email}</p>
          <div className="flex items-center mt-2 justify-between">
            {user.groups.length > 0 ? (
              <Badge variant="secondary">
                {user.groups.map((group) => group.name).join(", ")}
              </Badge>
            ) : (
              "-"
            )}
            <p className="text-sm text-gray-500">
              {formatDate(user.date_joined)}
            </p>
          </div>
        </>
      )}
    />
  );
};

export default ListContent;
